<template>
    <AdminLayout>
        <Head title="TRON Wallet Management" />
        
        <div class="p-6 max-w-7xl mx-auto">
            <!-- Header Section -->
            <div class="mb-8">
                <div class="flex items-center justify-between">
                    <div>
                        <h1 class="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                            <svg class="w-8 h-8 mr-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                            </svg>
                            TRON Wallet Management
                        </h1>
                        <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            Manage TRON wallet addresses for all users
                        </p>
                    </div>
                    <div>
                        <button @click="router.visit('/admin/tron-wallets/monitoring')" class="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200">
                            <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                            </svg>
                            Transaction Monitoring
                            <span class="ml-2 px-2 py-1 bg-white/20 rounded text-xs font-bold uppercase">New</span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Master Seed Status Card -->
            <div class="mb-6 rounded-xl shadow-lg overflow-hidden" :class="getStatusCardClass()">
                <div class="p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0">
                            <div class="w-16 h-16 rounded-full flex items-center justify-center" :class="getStatusIconBgClass()">
                                <svg v-if="!hasMasterSeed" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <svg v-else-if="regenerationStatus.needsRegeneration" class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <svg v-else class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                        <div class="ml-6 flex-1">
                            <h3 class="text-2xl font-bold" :class="getStatusTextClass()">
                                {{ getStatusTitle() }}
                            </h3>
                            <p class="mt-1 text-sm font-medium" :class="getStatusSubTextClass()">
                                {{ getStatusMessage() }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Critical Alert: Regeneration Required -->
            <div v-if="regenerationStatus.needsRegeneration" class="mb-6 rounded-xl shadow-lg overflow-hidden bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-2 border-red-500 dark:border-red-600 animate-pulse-slow">
                <div class="p-6">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <div class="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center">
                                <svg class="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                        </div>
                        <div class="ml-4 flex-1">
                            <h3 class="text-xl font-bold text-red-800 dark:text-red-200 flex items-center">
                                <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
                                </svg>
                                ⚠️ CRITICAL: Master Seed Changed - Wallets Out of Sync
                            </h3>
                            <p class="mt-2 text-sm text-red-700 dark:text-red-300 leading-relaxed">
                                <strong class="font-bold">Detection Result:</strong> {{ regenerationStatus.message }}
                            </p>
                            <div class="mt-3 bg-white/50 dark:bg-black/20 rounded-lg p-4 border border-red-300 dark:border-red-700">
                                <p class="text-sm text-red-900 dark:text-red-100 font-semibold mb-2">🚨 Immediate Action Required:</p>
                                <ul class="text-sm text-red-800 dark:text-red-200 space-y-1 ml-4 list-disc">
                                    <li>All existing TRON wallet addresses are <strong>INVALID</strong></li>
                                    <li>Users <strong>CANNOT receive deposits</strong> to their current addresses</li>
                                    <li>Funds sent to old addresses will be <strong>LOST</strong></li>
                                    <li>You <strong>MUST regenerate all wallets</strong> immediately</li>
                                </ul>
                            </div>
                            <div class="mt-4">
                                <button 
                                    @click="showRegenerationModal = true"
                                    class="inline-flex items-center px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl">
                                    <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Regenerate All Wallets Now
                                    <span class="ml-2 px-2 py-1 bg-white/20 rounded text-xs font-bold uppercase">Urgent</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                    <div class="p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Total Users</p>
                                <p class="text-3xl font-bold text-gray-900 dark:text-white mt-2">{{ totalUsers }}</p>
                            </div>
                            <div class="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div class="bg-blue-50 dark:bg-blue-900/20 px-6 py-2">
                        <p class="text-xs text-blue-600 dark:text-blue-400 font-medium">Non-admin users</p>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                    <div class="p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">With Address</p>
                                <p class="text-3xl font-bold text-green-600 dark:text-green-400 mt-2">{{ usersWithAddress }}</p>
                            </div>
                            <div class="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                                <svg class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div class="bg-green-50 dark:bg-green-900/20 px-6 py-2">
                        <p class="text-xs text-green-600 dark:text-green-400 font-medium">{{ Math.round((usersWithAddress / totalUsers) * 100) }}% coverage</p>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                    <div class="p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Missing Address</p>
                                <p class="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mt-2">{{ totalUsers - usersWithAddress }}</p>
                            </div>
                            <div class="w-14 h-14 bg-yellow-100 dark:bg-yellow-900/30 rounded-full flex items-center justify-center">
                                <svg class="w-8 h-8 text-yellow-600 dark:text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div class="bg-yellow-50 dark:bg-yellow-900/20 px-6 py-2">
                        <p class="text-xs text-yellow-600 dark:text-yellow-400 font-medium">Needs attention</p>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                    <div class="p-6">
                        <div class="flex items-center justify-between">
                            <div>
                                <p class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">System Status</p>
                                <p class="text-lg font-bold mt-2" :class="getSystemStatusColor()">
                                    {{ getSystemStatusText() }}
                                </p>
                            </div>
                            <div class="w-14 h-14 rounded-full flex items-center justify-center" :class="getSystemStatusBgClass()">
                                <svg v-if="!hasMasterSeed" class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                                <svg v-else-if="regenerationStatus.needsRegeneration" class="w-8 h-8 text-amber-600 dark:text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                                <svg v-else class="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                        </div>
                    </div>
                    <div class="px-6 py-2" :class="getSystemStatusFooterClass()">
                        <p class="text-xs font-medium" :class="getSystemStatusFooterTextClass()">
                            {{ getSystemStatusFooterText() }}
                        </p>
                    </div>
                </div>

                <!-- USDT Information Card -->
                <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300 overflow-hidden">
                    <div class="p-6">
                        <div class="flex items-center justify-between">
                            <div class="flex-1">
                                <p class="text-sm font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">USDT (TRC-20)</p>
                                <p class="text-2xl font-bold text-green-600 dark:text-green-400 mt-2">
                                    <span>$1.00</span>
                                </p>
                                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Pegged to USD</p>
                            </div>
                            <div>
                                <div class="p-3 bg-green-100 dark:bg-green-900/30 rounded-full">
                                    <svg class="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="bg-green-50 dark:bg-green-900/20 px-6 py-2">
                        <p class="text-xs text-green-600 dark:text-green-400 font-medium">
                            <span>Stablecoin - Always 1:1 with USD</span>
                        </p>
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="mb-6 flex flex-wrap gap-3">
                <a 
                    href="/admin/tron-wallets/export"
                    class="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 shadow-md hover:shadow-lg">
                    <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                    </svg>
                    Export CSV
                </a>

                <button 
                    v-if="hasMasterSeed && regenerationStatus.needsRegeneration"
                    @click="showRegenerationModal = true"
                    class="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-orange-600 text-white font-bold rounded-lg hover:from-red-600 hover:to-orange-700 transition-all duration-200 shadow-lg hover:shadow-xl animate-pulse">
                    <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Regenerate Wallets (URGENT)
                    <span class="ml-2 px-2 py-1 bg-white/30 rounded text-xs font-bold uppercase">Seed Changed</span>
                </button>

                <button 
                    v-else-if="hasMasterSeed"
                    @click="showRegenerationModal = true"
                    class="inline-flex items-center px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-600 text-white font-bold rounded-lg hover:from-amber-600 hover:to-orange-700 transition-all duration-200 shadow-lg hover:shadow-xl">
                    <svg class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Regenerate Wallets
                    <span class="ml-2 px-2 py-0.5 bg-white/20 rounded text-xs font-semibold">Optional</span>
                </button>

                <button 
                    @click="refreshData"
                    :disabled="isLoading"
                    class="inline-flex items-center px-6 py-3 bg-white dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-50 shadow-md hover:shadow-lg">
                    <svg :class="{'animate-spin': isLoading}" class="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                </button>
            </div>

            <!-- Users Table -->
            <div class="bg-white dark:bg-gray-800 shadow-xl rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                <div class="px-6 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-white">User TRON Addresses</h3>
                    <p class="text-sm text-gray-600 dark:text-gray-400 mt-1">Complete list of all users with their TRON wallet information</p>
                </div>
                <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-100 dark:bg-gray-900">
                            <tr>
                                <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">ID</th>
                                <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">User</th>
                                <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">TRON Address</th>
                                <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Wallet Info</th>
                                <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Index</th>
                                <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Created</th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="user in users" :key="user.id" class="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors duration-150">
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-bold text-sm">
                                        {{ user.id }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <div class="flex items-center">
                                        <div class="flex-shrink-0 h-10 w-10">
                                            <div class="h-10 w-10 rounded-full bg-gradient-to-br from-purple-400 to-pink-600 flex items-center justify-center text-white font-bold">
                                                {{ (user.display_name || user.name)?.charAt(0).toUpperCase() || '?' }}
                                            </div>
                                        </div>
                                        <div class="ml-4">
                                            <div class="text-sm font-semibold text-gray-900 dark:text-white">{{ user.display_name || user.name }}</div>
                                            <div class="text-sm text-gray-500 dark:text-gray-400">{{ user.email }}</div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4">
                                    <div v-if="user.tron_wallet_address" class="flex items-center gap-2">
                                        <code class="text-xs bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-md text-gray-800 dark:text-gray-200 font-mono">
                                            {{ user.tron_wallet_address }}
                                        </code>
                                        <button 
                                            @click="copyToClipboard(user.tron_wallet_address)"
                                            class="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors duration-150"
                                            title="Copy address">
                                            <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                        </button>
                                    </div>
                                    <span v-else class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300">
                                        Not assigned
                                    </span>
                                </td>
                                <td class="px-6 py-4">
                                    <div v-if="user.wallet_address">
                                        <div class="flex items-center gap-2 mb-1">
                                            <code class="text-xs bg-gray-100 dark:bg-gray-700 px-3 py-1.5 rounded-md text-gray-800 dark:text-gray-200 font-mono">
                                                {{ user.wallet_address }}
                                            </code>
                                            <button 
                                                @click="copyToClipboard(user.wallet_address)"
                                                class="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded transition-colors duration-150"
                                                title="Copy wallet address">
                                                <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                </svg>
                                            </button>
                                        </div>
                                        <div class="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                                            <span class="font-medium">{{ user.wallet_name || 'N/A' }}</span>
                                            <span v-if="user.wallet_balance !== null" class="inline-flex items-center px-2 py-0.5 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-semibold">
                                                {{ parseFloat(user.wallet_balance).toFixed(2) }} USDT
                                            </span>
                                        </div>
                                    </div>
                                    <span v-else class="text-sm text-gray-500 dark:text-gray-400">No wallet</span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300">
                                        {{ user.derivation_index ?? 'N/A' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {{ user.created_at }}
                                </td>
                            </tr>
                            <tr v-if="users.length === 0">
                                <td colspan="6" class="px-6 py-12 text-center">
                                    <svg class="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                    </svg>
                                    <p class="mt-4 text-sm text-gray-500 dark:text-gray-400">No users found</p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- Master Seed Regeneration Modal -->
            <MasterSeedRegenerationModal
                :show="showRegenerationModal"
                @close="showRegenerationModal = false"
                @regenerated="handleRegenerationComplete"
            />

            <!-- Toast Notification -->
            <div v-if="toast.show" class="fixed bottom-4 right-4 z-50 animate-slide-in">
                <div class="rounded-xl p-4 shadow-2xl max-w-md" :class="toast.type === 'success' ? 'bg-green-50 dark:bg-green-900/30 border-2 border-green-500' : 'bg-red-50 dark:bg-red-900/30 border-2 border-red-500'">
                    <div class="flex items-start">
                        <div class="flex-shrink-0">
                            <svg v-if="toast.type === 'success'" class="h-6 w-6 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                            </svg>
                            <svg v-else class="h-6 w-6 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                            </svg>
                        </div>
                        <div class="ml-3 flex-1">
                            <p class="text-sm font-bold" :class="toast.type === 'success' ? 'text-green-800 dark:text-green-200' : 'text-red-800 dark:text-red-200'">
                                {{ toast.message }}
                            </p>
                        </div>
                        <div class="ml-4 flex-shrink-0">
                            <button @click="toast.show = false" class="inline-flex rounded-md p-1.5" :class="toast.type === 'success' ? 'text-green-500 hover:bg-green-200 dark:hover:bg-green-800' : 'text-red-500 hover:bg-red-200 dark:hover:bg-red-800'">
                                <span class="sr-only">Dismiss</span>
                                <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                                    <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </AdminLayout>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { Head, router, usePage } from '@inertiajs/vue3';
import axios from 'axios';
import AdminLayout from '@/Layouts/AdminLayout/AdminLayout.vue';
import MasterSeedRegenerationModal from '@/Components/Admin/MasterSeedRegenerationModal.vue';

const props = defineProps({
    users: Array,
    hasMasterSeed: Boolean,
    totalUsers: Number,
});

const page = usePage();
const isLoading = ref(false);
const showRegenerationModal = ref(false);
const regenerationStatus = ref({
    needsRegeneration: false,
    message: '',
    checking: true,
});
const exchangeRate = ref({
    rate: null,
    updated_at: null,
    loading: true,
    updating: false,
});
const toast = ref({
    show: false,
    type: 'success',
    message: '',
});
const lastFlash = ref({ success: null, error: null, message: null });

const usersWithAddress = computed(() => {
    return props.users.filter(user => user.tron_wallet_address).length;
});

const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
        showToast('success', 'Address copied to clipboard!');
    }).catch(() => {
        showToast('error', 'Failed to copy address');
    });
};

const showToast = (type, message) => {
    toast.value = {
        show: true,
        type,
        message,
    };
    setTimeout(() => {
        toast.value.show = false;
    }, 5000);
};

const handleFlashMessages = (flash) => {
    if (!flash) {
        return;
    }

    if (flash.success && flash.success !== lastFlash.value.success) {
        showToast('success', flash.success);
    }

    if (flash.error && flash.error !== lastFlash.value.error) {
        showToast('error', flash.error);
    }

    if (flash.message && flash.message !== lastFlash.value.message) {
        showToast('success', flash.message);
    }

    lastFlash.value = {
        success: flash.success ?? null,
        error: flash.error ?? null,
        message: flash.message ?? null,
    };
};

handleFlashMessages(page.props.flash);

// Fetch exchange rate
const fetchExchangeRate = async () => {
    try {
        exchangeRate.value.loading = true;
        const response = await axios.get('/admin/tron-wallets/exchange-rate');
        if (response.data.success) {
            exchangeRate.value.rate = response.data.rate;
            exchangeRate.value.updated_at = response.data.updated_at;
        }
    } catch (error) {
        console.error('Failed to fetch exchange rate:', error);
    } finally {
        exchangeRate.value.loading = false;
    }
};

// Update exchange rate manually
const updateExchangeRate = async () => {
    try {
        exchangeRate.value.updating = true;
        const response = await axios.post('/admin/tron-wallets/update-rate');
        if (response.data.success) {
            exchangeRate.value.rate = response.data.rate;
            const change = response.data.change_percent;
            const changeText = change > 0 ? `+${change.toFixed(2)}%` : `${change.toFixed(2)}%`;
            showToast('success', `Rate updated: $${response.data.rate.toFixed(6)} (${changeText})`);
            fetchExchangeRate(); // Refresh to get updated timestamp
        }
    } catch (error) {
        showToast('error', 'Failed to update exchange rate');
        console.error('Failed to update exchange rate:', error);
    } finally {
        exchangeRate.value.updating = false;
    }
};

// Format time ago
const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
};

watch(() => page.props.flash, (flash) => {
    handleFlashMessages(flash);
}, { deep: true });

// Fetch exchange rate on mount
fetchExchangeRate();

// Auto-refresh exchange rate every 5 minutes
let rateInterval = setInterval(fetchExchangeRate, 5 * 60 * 1000);

// Cleanup on unmount
onBeforeUnmount(() => {
    if (rateInterval) clearInterval(rateInterval);
});

const refreshData = () => {
    isLoading.value = true;
    router.reload({
        only: ['users', 'hasMasterSeed', 'totalUsers'],
        onFinish: () => {
            isLoading.value = false;
            showToast('success', 'Data refreshed successfully');
        },
    });
};

const handleRegenerationComplete = (results) => {
    showToast('success', `Regeneration completed: ${results.success} successful, ${results.failed} failed`);
    regenerationStatus.value.needsRegeneration = false;
    refreshData();
};

const checkRegenerationStatus = async () => {
    if (!props.hasMasterSeed) {
        regenerationStatus.value.checking = false;
        return;
    }

    try {
        console.log('🔍 Checking regeneration status...');
        const response = await fetch('/admin/tron-wallets/check-regeneration', {
            headers: {
                'X-Requested-With': 'XMLHttpRequest',
                'Accept': 'application/json',
            },
        });

        if (response.ok) {
            const data = await response.json();
            console.log('📊 Regeneration check response:', data);
            
            // Backend returns 'needs_regeneration' with underscore
            const needsRegen = data.needs_regeneration || data.needsRegeneration || false;
            
            regenerationStatus.value = {
                needsRegeneration: needsRegen,
                message: data.message,
                checking: false,
                totalUsers: data.total_users ?? props.totalUsers ?? 0,
                walletsWithAddresses: data.users_with_wallets ?? null,
                sampleUserId: data.sample_user_id ?? null,
            };

            console.log('✅ Regeneration status updated:', regenerationStatus.value);

            if (needsRegen) {
                console.log('⚠️ ALERT: Master seed changed!');
                showToast('error', '⚠️ Master seed changed! Wallet regeneration required.');
            } else {
                console.log('✓ All wallets synced with current master seed');
            }
        } else {
            console.error('❌ API response not OK:', response.status);
        }
    } catch (error) {
        console.error('❌ Failed to check regeneration status:', error);
        regenerationStatus.value.checking = false;
    }
};

const getStatusCardClass = () => {
    if (!props.hasMasterSeed) {
        return 'bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-2 border-red-200 dark:border-red-800';
    }
    if (regenerationStatus.value.needsRegeneration) {
        return 'bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border-2 border-amber-500 dark:border-amber-700';
    }
    return 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-200 dark:border-green-800';
};

const getStatusIconBgClass = () => {
    if (!props.hasMasterSeed) return 'bg-red-500';
    if (regenerationStatus.value.needsRegeneration) return 'bg-amber-500';
    return 'bg-green-500';
};

const getStatusTextClass = () => {
    if (!props.hasMasterSeed) return 'text-red-800 dark:text-red-200';
    if (regenerationStatus.value.needsRegeneration) return 'text-amber-800 dark:text-amber-200';
    return 'text-green-800 dark:text-green-200';
};

const getStatusSubTextClass = () => {
    if (!props.hasMasterSeed) return 'text-red-700 dark:text-red-300';
    if (regenerationStatus.value.needsRegeneration) return 'text-amber-700 dark:text-amber-300';
    return 'text-green-700 dark:text-green-300';
};

const getStatusTitle = () => {
    if (!props.hasMasterSeed) return '✗ Master Seed Not Configured';
    if (regenerationStatus.value.needsRegeneration) return '⚠️ Master Seed Changed';
    return '✓ Master Seed Configured';
};

const getStatusMessage = () => {
    if (!props.hasMasterSeed) return 'Please configure TRON master seed in Payment Gateways first';
    if (regenerationStatus.value.needsRegeneration) return 'Master seed has been modified - wallet regeneration required';
    return 'TRON wallet system is operational and ready';
};

const getSystemStatusText = () => {
    if (!props.hasMasterSeed) return 'Not Configured';
    if (regenerationStatus.value.needsRegeneration) return 'Out of Sync';
    return 'Operational';
};

const getSystemStatusColor = () => {
    if (!props.hasMasterSeed) return 'text-red-600 dark:text-red-400';
    if (regenerationStatus.value.needsRegeneration) return 'text-amber-600 dark:text-amber-400';
    return 'text-green-600 dark:text-green-400';
};

const getSystemStatusBgClass = () => {
    if (!props.hasMasterSeed) return 'bg-red-100 dark:bg-red-900/30';
    if (regenerationStatus.value.needsRegeneration) return 'bg-amber-100 dark:bg-amber-900/30';
    return 'bg-green-100 dark:bg-green-900/30';
};

const getSystemStatusFooterClass = () => {
    if (!props.hasMasterSeed) return 'bg-red-50 dark:bg-red-900/20';
    if (regenerationStatus.value.needsRegeneration) return 'bg-amber-50 dark:bg-amber-900/20';
    return 'bg-green-50 dark:bg-green-900/20';
};

const getSystemStatusFooterTextClass = () => {
    if (!props.hasMasterSeed) return 'text-red-600 dark:text-red-400';
    if (regenerationStatus.value.needsRegeneration) return 'text-amber-600 dark:text-amber-400';
    return 'text-green-600 dark:text-green-400';
};

const getSystemStatusFooterText = () => {
    if (!props.hasMasterSeed) return 'Configuration needed';
    if (regenerationStatus.value.needsRegeneration) return 'Regeneration required';
    return 'Ready to use';
};

// Check regeneration status when component mounts
checkRegenerationStatus();
</script>

<style scoped>
@keyframes slide-in {
    from {
        transform: translateX(100%);
        opacity: 0;
    }
    to {
        transform: translateX(0);
        opacity: 1;
    }
}

.animate-slide-in {
    animation: slide-in 0.3s ease-out;
}

@keyframes pulse-slow {
    0%, 100% {
        opacity: 1;
    }
    50% {
        opacity: 0.85;
    }
}

.animate-pulse-slow {
    animation: pulse-slow 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
</style>
